import { useTranslation } from 'react-i18next';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import {
  RegisteredAssetListFilters,
  RegisteredAssetModel,
  useListAssetsQuery,
} from '@casops/store/apis/registeredAssetsApi';
import { ModelName } from '@casops/store/api';
import FilterButton from '@casops/components/inputs/filter-button/FilterButton';
import useFilters from '@casops/filters/useFilters';
import { FilterGroup } from '@casops/filters/types';
import { useAssetsColumnDefs } from '@casops/routes/assets/assets/useAssetsColumnDefs';
import { ASSET_KEY } from '@casops/navigation';
import useRowFocus from '@casops/components/tables/grid/useRowFocus';
import { ASSETS_HOME_SLICE_KEY } from '@casops/store/slices/gridSlice';

type Model = RegisteredAssetModel;

const modelName = ModelName.Asset;

export function Assets() {
  const { t } = useTranslation();
  const filters = useFilters<RegisteredAssetListFilters>(FilterGroup.AssetsList);

  const columnDefs = useAssetsColumnDefs();

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListAssetsQuery(
    { query: filters },
    {
      refetchOnMountOrArgChange: 60,
    },
  );

  const { items, countAll, countFilter } = data ?? {};

  const { hasData, noDataDescription } = useTableData<Model>({
    data: items,
    ...requestRemainder,
    noResultsDescription,
  });

  const { rowClassRules, onRowClick, ensureNodeVisibleCallback, ensureNodeVisibleKey } =
    useRowFocus<Model>(ASSET_KEY);

  return (
    <CollectionView<Model>
      sliceKey={ASSETS_HOME_SLICE_KEY}
      modelName={modelName}
      columnDefs={columnDefs}
      rowData={items}
      hasData={hasData}
      noDataDescription={noDataDescription}
      onRowClick={onRowClick}
      rowClassRules={rowClassRules}
      ensureNodeVisibleCallback={ensureNodeVisibleCallback}
      ensureNodeVisibleKey={ensureNodeVisibleKey}
      showSummaryInTable={true}
      filterProps={{
        filterGroup: FilterGroup.AssetsList,
        countAll,
        countFilter,
      }}
      actions={<FilterButton filters={FilterGroup.AssetsList} disabled={false} />}
    />
  );
}

export default Assets;
